import { useEffect, useState } from 'react'
import { useProduct } from '../context/ProductContext'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/ui/Card'
import { SkeletonCard } from '../components/ui/Skeleton'
import { GoLivePanel } from '../components/GoLivePanel'
import { OnboardingChecklist } from '../components/onboarding/OnboardingChecklist'
import { DashboardSalesMetrics } from '../components/dashboard/DashboardSalesMetrics'
import { DashboardLowStock } from '../components/dashboard/DashboardLowStock'
import { DashboardQuickActions, staffModuleFlags } from '../components/dashboard/DashboardQuickActions'
import { useOrganisationDisplayName } from '../context/OrganisationDisplayContext'
import { useCurrentStore } from '../context/CurrentStoreContext'
import { staffHasModule } from '../config/staffModules'
import { shouldShowOnboardingChecklist } from '../lib/onboardingChecklist'
import { DEFAULT_LOW_STOCK_THRESHOLD, normalizeLowStockThreshold } from '../lib/inventoryStock'
import { useDashboardSalesStats } from '../hooks/useDashboardSalesStats'
import { useLowStockAlert } from '../hooks/useLowStockAlert'

export default function DashboardHome() {
  const { storeRecord: store, applyStoreRecord, refreshStores } = useCurrentStore()
  const { user } = useAuth()
  const { organisationDisplayName } = useOrganisationDisplayName()
  const { listTotal: productTotal, fetchProducts } = useProduct()
  const [catalogReady, setCatalogReady] = useState(false)

  const modules = user?.modules
  const showOnboarding = shouldShowOnboardingChecklist(store, user?.role)
  const showGoLive = user?.role === 'org_admin' && Boolean(store?.id)
  const canCatalog = user?.role === 'org_admin' || staffHasModule(modules, 'catalog')
  const canSales = user?.role === 'org_admin' || staffHasModule(modules, 'sales')
  const flags = staffModuleFlags(user?.role, modules, Boolean(store?.posEnabled))
  const { stats, loading: salesStatsLoading, error: salesStatsError } = useDashboardSalesStats(
    store?.id,
    canSales,
  )
  const lowStockThreshold = normalizeLowStockThreshold(
    store?.lowStockThreshold ?? DEFAULT_LOW_STOCK_THRESHOLD,
  )
  useLowStockAlert(canCatalog, lowStockThreshold, store?.id)

  useEffect(() => {
    if (!canCatalog) {
      setCatalogReady(true)
      return
    }
    setCatalogReady(false)
    fetchProducts({ page: 1, limit: 1 }, { totalsOnly: true })
      .catch(() => {})
      .finally(() => setCatalogReady(true))
  }, [fetchProducts, canCatalog, store?.id])

  return (
    <div className="space-y-8">
      {organisationDisplayName ? (
        <div className="space-y-1">
          <h2
            className="truncate text-2xl font-semibold tracking-tight text-foreground sm:text-[1.65rem]"
            title={organisationDisplayName}
          >
            Welcome to {organisationDisplayName}
          </h2>
          <p className="text-sm text-muted-foreground">You&apos;re managing this organisation&apos;s store.</p>
        </div>
      ) : null}

      {showOnboarding && store ? (
        <OnboardingChecklist
          store={store}
          onStoreUpdated={applyStoreRecord}
        />
      ) : null}

      {showGoLive && store?.id ? (
        <GoLivePanel storeId={store.id} onWentLive={() => void refreshStores()} />
      ) : null}

      {canSales ? (
        <DashboardSalesMetrics stats={stats} loading={salesStatsLoading} error={salesStatsError} />
      ) : null}

      {canCatalog ? (
        <>
          <DashboardLowStock threshold={lowStockThreshold} />
          <section>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Catalog</h3>
            {!catalogReady ? (
              <SkeletonCard />
            ) : (
              <Card className="p-5" padding={false}>
                <p className="text-sm font-medium text-muted-foreground">Total products</p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-foreground tabular-nums">{productTotal}</p>
              </Card>
            )}
          </section>
        </>
      ) : null}

      {!canSales && !canCatalog ? (
        <Card className="p-5">
          <p className="text-sm text-muted-foreground">
            Your assigned modules do not include catalog or sales metrics. Use the sidebar to open an area you can access.
          </p>
        </Card>
      ) : null}

      <DashboardQuickActions
        canCatalog={flags.catalog}
        canSales={flags.sales}
        canPayments={flags.payments}
        canStorefront={flags.storefront}
        canPos={flags.pos}
        pendingFulfillmentCount={stats?.pendingFulfillmentCount ?? 0}
      />
    </div>
  )
}
